import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";
import type { CartLine } from "../cart.model";

export interface CartLineUnavailableProps {
  line: CartLine;
  onRemove: () => void;
}

/**
 * Una línea cuyo producto ya no está en el catálogo público: se
 * despublicó, se archivó o se borró mientras el carrito esperaba guardado
 * en el navegador de alguien.
 *
 * ── Por qué no se borra sola ─────────────────────────────────────────
 * Tentador, y equivocado. El catálogo también "falta" cuando la petición
 * falló o todavía no llegó, y una limpieza automática vaciaría el carrito
 * de alguien por un error de red de dos segundos. Aquí se ENSEÑA que esa
 * línea ya no se puede comprar y se ofrece quitarla; el borrado lo decide
 * la persona.
 *
 * ── Y por qué se ve distinta ─────────────────────────────────────────
 * Sin miniatura, sin precio y sin selector de cantidad: no hay nada que
 * elegir. Dejar los controles puestos y desactivados invitaría a pelearse
 * con ellos.
 */
export function CartLineUnavailable({ line, onRemove }: CartLineUnavailableProps) {
  const { t } = useTranslation();

  return (
    <li className="flex items-start justify-between gap-3 py-4">
      <div className="min-w-0">
        {/* El slug es lo único que queda para identificarlo. Se pinta en
            monoespaciada para que se lea como un identificador y no como
            el nombre del producto, que no tenemos. */}
        <p className="truncate font-mono text-[13px] font-semibold text-ds-ink">
          {line.slug}
        </p>
        <p className="mt-0.5 text-[12px] text-ds-ink-3">
          {t("Este producto ya no está disponible")}
        </p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        iconOnly
        leftIcon={<Trash2 />}
        onClick={onRemove}
        aria-label={t("Quitar {{producto}} del carrito", { producto: line.slug })}
        className="shrink-0 border-0 text-ds-ink-3 hover:text-ds-terracotta"
      />
    </li>
  );
}
